import { useRef, useState } from 'react';
import { RichTextField } from '@/components/Admin/RichTextField';
import { dirtyStyle } from '@/components/Admin/Autosave/AutosaveTextInput';

interface AutosaveRichTextInputProps {
  /** The stored value: HTML, or legacy plain text (newlines become paragraphs). */
  currentValue: string;
  /** Called when the user commits a changed value (focus leaving the editor). */
  onCommitChange: (value: string) => void;
  placeholder?: string;
}

/**
 * `RichTextField` with the same autosave contract as `AutosaveTextInput`: the
 * editor writes to a local draft on every keystroke, and only blur commits it.
 *
 * Committing per keystroke would be wrong here twice over — a Firestore write
 * and an undo step for every character — so the draft stays local until focus
 * leaves the widget, exactly like the textarea this replaced.
 */
export function AutosaveRichTextInput({
  currentValue,
  onCommitChange,
  placeholder,
}: AutosaveRichTextInputProps) {
  const [localValue, setLocalValue] = useState(currentValue);
  const escapePressedRef = useRef(false);

  // Reset the draft when the underlying value changes (load, undo/redo). Done
  // during render rather than in an effect to avoid a cascading re-render.
  const [lastSynced, setLastSynced] = useState(currentValue);
  if (currentValue !== lastSynced) {
    setLastSynced(currentValue);
    setLocalValue(currentValue);
  }

  const isDirty = localValue !== currentValue;

  function handleBlur() {
    if (escapePressedRef.current) {
      // Blur triggered by Escape — the draft was already discarded.
      escapePressedRef.current = false;
      return;
    }
    if (localValue === currentValue) return;
    onCommitChange(localValue);
  }

  return (
    <RichTextField
      value={localValue}
      placeholder={placeholder}
      onChange={setLocalValue}
      onBlur={handleBlur}
      // The blur this causes runs in the same batch as the state reset below, so
      // the flag — not `localValue` — is what tells handleBlur to stand down.
      onEscape={() => {
        escapePressedRef.current = true;
        setLocalValue(currentValue);
      }}
      className={isDirty ? dirtyStyle : undefined}
    />
  );
}
